import {FormattedMessage} from 'react-intl';

import classNames from 'classnames';

import {Avatar} from '@time-webkit/all/atoms/avatar';

import {type Post, type PostPreviewMetadata} from 'mattermost-redux/types/posts';
import UserProfileComponent from 'components/user_profile';
import {type UserProfile} from 'mattermost-redux/types/users';
import * as PostUtils from 'utils/post_utils';
import * as Utils from 'utils/utils';
import PostMessageView from 'components/post_view/post_message_view';

import Timestamp from 'components/timestamp';
import PostAttachmentContainer from '../post_attachment_container/post_attachment_container';
import FileAttachmentListContainer from 'components/file_attachment_list';
import PostAttachmentOpenGraph from 'components/post_view/post_attachment_opengraph';

import TimeMessageLogo from 'components/widgets/icons/time_message_logo';
import {General} from 'mattermost-redux/constants';
import {markChannelLinkClicked, markThreadLinkClicked} from 'packages/performance';

export type Props = {
    currentTeamUrl: string;
    channelDisplayName?: string;
    user?: UserProfile | null;
    userDisplayName?: string;
    previewPost?: Post;
    metadata: PostPreviewMetadata;
    overrideIconURL?: string;
    overrideUsername?: string;
    isEmbedVisible: boolean;
    compactDisplay: boolean;
    handleFileDropdownOpened?: (open: boolean) => void;
    actions: {
        toggleEmbedVisibility: (id: string) => void;
    };
};

const PostMessagePreview = (props: Props) => {
    const {
        overrideUsername,
        overrideIconURL,
        currentTeamUrl,
        channelDisplayName,
        user,
        previewPost,
        metadata,
        isEmbedVisible,
        compactDisplay,
        handleFileDropdownOpened,
    } = props;

    if (!previewPost) {
        return null;
    }

    const toggleEmbedVisibility = () => {
        if (previewPost) {
            props.actions.toggleEmbedVisibility(previewPost.id);
        }
    };

    const isBot = Boolean(user && user.is_bot);
    const isSystemMessage = PostUtils.isSystemMessage(previewPost);
    const fromWebhook = PostUtils.isFromWebhook(previewPost);
    const profileSrc = Utils.imageURLForUser(user?.id ?? '', user?.last_picture_update);
    const src = overrideIconURL || profileSrc;

    let avatar = null;

    if (!compactDisplay) {
        if (isSystemMessage && !fromWebhook && !isBot) {
            avatar = (<TimeMessageLogo className='icon'/>);
        } else if (user?.id) {
            avatar = (
                <Avatar
                    username={props.userDisplayName || user.username}
                    size={24}
                    src={src}
                    className={'avatar-post-preview'}
                />
            );
        } else {
            avatar = (
                <Avatar
                    size={24}
                    src={src}
                    className={'avatar-post-preview'}
                />
            );
        }
    }

    let fileAttachmentPreview = null;

    if (((previewPost.file_ids && previewPost.file_ids.length > 0) || (previewPost.filenames && previewPost.filenames.length > 0))) {
        fileAttachmentPreview = (
            <FileAttachmentListContainer
                post={previewPost}
                compactDisplay={compactDisplay}
                isInPermalink={true}
                handleFileDropdownOpened={handleFileDropdownOpened}
            />
        );
    }

    let urlPreview = null;

    if (previewPost && previewPost.metadata && previewPost.metadata.embeds) {
        const embed = previewPost.metadata.embeds[0];

        if (embed && embed.type === 'opengraph') {
            urlPreview = (
                <PostAttachmentOpenGraph
                    postId={previewPost.id}
                    link={embed.url}
                    isEmbedVisible={isEmbedVisible}
                    post={previewPost}
                    toggleEmbedVisibility={toggleEmbedVisibility}
                    isInPermalink={true}
                />
            );
        }
    }

    let teamUrl = `/${metadata.team_name}`;
    if (metadata.channel_type === General.DM_CHANNEL || metadata.channel_type === General.GM_CHANNEL) {
        teamUrl = currentTeamUrl;
    }

    const link = `${teamUrl}/pl/${metadata.post_id}`;
    const markLinkClicked = () => {
        const detail = {
            sourceType: 'link',
            channelId: metadata.channel_id,
            channelName: metadata.channel_display_name,
            channelType: metadata.channel_type,
        };
        if (previewPost.root_id) {
            markThreadLinkClicked({detail});
            return;
        }
        markChannelLinkClicked({detail});
    };

    return (
        <PostAttachmentContainer
            className='permalink'
            link={link}
            onClick={markLinkClicked}
        >
            <div className='post-preview'>
                <div className='post-preview__header'>
                    <div className='col col__name'>
                        <div className='post__img'>
                            <span className='profile-icon'>
                                {avatar}
                            </span>
                        </div>
                    </div>
                    <div className={classNames('col col__name', 'permalink--username')}>
                        <UserProfileComponent
                            userId={user?.id}
                            hasMention={true}
                            disablePopover={true}
                            overwriteName={overrideUsername}
                        />
                    </div>
                    <div className='col'>
                        <Timestamp
                            value={previewPost.create_at}
                            units={[
                                'now',
                                'minute',
                                'hour',
                                'day',
                            ]}
                            useTime={false}
                            day={'numeric'}
                            className='post-preview__time'
                        />
                    </div>
                </div>
                <PostMessageView
                    post={previewPost}
                    overflowType='ellipsis'
                    maxHeight={105}
                />
                {urlPreview}
                {fileAttachmentPreview}
                <div className='post__preview-footer'>
                    <p>
                        <FormattedMessage
                            id='post_message_preview.channel'
                            defaultMessage='Only visible to users in ~{channel}'
                            values={{
                                channel: channelDisplayName,
                            }}
                        />
                    </p>
                </div>
            </div>
        </PostAttachmentContainer>
    );
};

export default PostMessagePreview;
